//================================================================
//  Page: My Requests
//================================================================

//  Purpose: This page shows all commercial requests assigned to the current user

//  Example:
//    <MyTasks></MyTasks>    

//================================================================

// Libraries
import React, { useReducer, useEffect, useContext } from 'react';

// Contexts
import { GetUser } from '../../Library/GlobalContexts';
import { SetToast } from '../../Library/GlobalContexts';

// Components
import PageComponent from '../../Components/PageComponent/PageComponent';
import TabView from '../../Components/TabView/TabView';
import ReviewAndNegotiate from './Tabs/ReviewAndNegotiate';
import RaisePO from './Tabs/RaisePO';
import Execute from './Tabs/Execute';
import Completed from './Tabs/Completed';
import Cancelled from './Tabs/Cancelled';
import All from './Tabs/All';

// Tabs

// Functions
import QueryListener from '../../Library/QueryListener';

// Images

// CSS
import './MyTasks.css';


export default function MyTasks() {

  //------------------------------------------------------
  //  useContext
  //------------------------------------------------------

  const getUser = useContext(GetUser);

  const setToast = useContext(SetToast);

  //------------------------------------------------------
  //  useReducer
  //------------------------------------------------------

  // Used to store the total requests per stage/view
  const [requests, setRequests] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      'reviewAndNegotiateRequests': [],
      'reviewAndNegotiateRequestsFiltered': [],
      'raisePORequests': [],
      'raisePORequestsFiltered': [],
      'executeRequests': [],
      'executeRequestsFiltered': [],
    }
  );

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Query Listener
  // - Get all commercial requests in each stage of current logged in user
  useEffect(() => {

    const stages = [
      { key: 'reviewAndNegotiateRequests', stage: 'Review and Negotiate', status: 'In Progress' },
      { key: 'raisePORequests', stage: 'Raise PO', status: 'In Progress' },
      { key: 'executeRequests', stage: 'Execute', status: 'In Progress' },
    ];

    function onLoadChange(documents) {

      // Filter requests
      const requests = {};

      stages.forEach(stage => {
        requests[stage.key] = documents.filter(document => {
          if (stage.additionalFilter) {
            return document.stage === stage.stage && document.status === stage.status && document[stage.additionalFilter[0]] === stage.additionalFilter[1];
          } else {
            return document.stage === stage.stage && document.status === stage.status;
          }
        });
      });

      setRequests(requests);

    }

    function onError(error) {
      console.log(error);

      // Set error on toast
      setToast({
        'type': 'error',
        'message': 'Unable to load requests',
      });

    }

    const unsubscribe = QueryListener('requests', [
      ['assignmentgroup', '==', 'Commercial'],
      ['assignedto.email', '==', getUser?.emailaddress]
    ], onLoadChange, onLoadChange, onError);

    return () => {
      unsubscribe();
    };

    // eslint-disable-next-line
  }, [getUser]);

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <PageComponent
      body={
        <div>

          <TabView
            defaultView={1}

            //------------------------------------------------------
            //  Review and Negotiate
            //------------------------------------------------------

            oneTitle={
              <div className='flex flex-row gap-2'>
                Review and Negotiate
                <div className='flex items-center justify-center h-[22px] w-[22px] rounded-full bg-[#006866] text-white text-[12px] font-bold' hidden={requests?.reviewAndNegotiateRequests?.length === 0}>
                  {requests?.reviewAndNegotiateRequests?.length}
                </div>
              </div>
            }
            oneRoute={'/myrequests?view=reviewandnegotiate'}
            oneContent={
              <ReviewAndNegotiate
                requests={requests}
                setRequests={setRequests}
              ></ReviewAndNegotiate>
            }

            //------------------------------------------------------
            // Execute
            //------------------------------------------------------

            twoTitle={
              <div className='flex flex-row gap-2'>
                Execute
                <div className='flex items-center justify-center h-[22px] w-[22px] rounded-full bg-[#006866] text-white text-[12px] font-bold' hidden={requests?.executeRequests?.length === 0}>
                  {requests?.executeRequests?.length}
                </div>
              </div>
            }
            twoRoute={'/myrequests?view=execute'}
            twoContent={
              <Execute
                requests={requests}
                setRequests={setRequests}
              ></Execute>
            }

            //------------------------------------------------------
            // Raise PO
            //------------------------------------------------------

            threeTitle={
              <div className='flex flex-row gap-2'>
                Raise PO
                <div className='flex items-center justify-center h-[22px] w-[22px] rounded-full bg-[#006866] text-white text-[12px] font-bold' hidden={requests?.raisePORequests?.length === 0}>
                  {requests?.raisePORequests?.length}
                </div>
              </div>
            }
            threeRoute={'/myrequests?view=raisepo'}
            threeContent={
              <RaisePO
                requests={requests}
                setRequests={setRequests}
              ></RaisePO>
            }

            //------------------------------------------------------
            //  Completed
            //------------------------------------------------------

            fourTitle={'Completed'}
            fourRoute={'/myrequests?view=completed'}
            fourContent={
              <Completed></Completed>
            }

            //------------------------------------------------------
            //  Cancelled
            //------------------------------------------------------

            fiveTitle={'Cancelled'}
            fiveRoute={'/myrequests?view=cancelled'}
            fiveContent={
              <Cancelled></Cancelled>
            }

            //------------------------------------------------------
            //  All
            //------------------------------------------------------

            sixTitle={'All'}
            sixRoute={'/myrequests?view=all'}
            sixContent={
              <All></All>
            }


          ></TabView>

        </div>
      }
    ></PageComponent>
  )
}
